<template>
  <AppLayout>
    <div class="flex flex-col gap-6">
      <!-- Toolbar: back, title, download -->
      <div class="flex items-center gap-4 flex-wrap">
        <RouterLink to="/products" class="shrink-0">
          <Button
            v-tooltip.bottom="'Back to products'"
            variant="text"
            severity="secondary"
            icon="pi pi-arrow-left"
            rounded
            aria-label="Back to products"
          />
        </RouterLink>
        <h2 v-if="product" class="text-surface-0 text-xl font-medium m-0 truncate flex-1 min-w-0">
          {{ product.code }}
        </h2>
        <a
          v-if="product"
          :href="downloadUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="shrink-0 no-underline"
        >
          <Button
            v-tooltip.bottom="'Download selected images'"
            variant="text"
            severity="secondary"
            icon="pi pi-download"
            rounded
            aria-label="Download"
          />
        </a>
      </div>

      <template v-if="product">
        <div
          class="flex flex-row flex-wrap gap-3 product-detail-grid"
          :style="{ '--detail-image-size': `${detailImageSizePx}px` }"
        >
          <ProductImage
            v-for="image in images"
            :key="image.id"
            :product-id="route.params.id.toString()"
            :image="image"
            v-model:selected-images="selectedImages"
            class="product-detail-image-card"
            @delete="confirmDeleteImage(image)"
            @toggle-attention="toggleImageAttention(image)"
          />
        </div>
      </template>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, RouterLink } from 'vue-router';
import AppLayout from '@/components/AppLayout.vue';
import ProductImage from '@/modules/products/product-detail/ProductImage.vue';
import Button from 'primevue/button';
import { useConfirm } from 'primevue/useconfirm';
import type { ImageDto, ProductDto } from '@/api/generated';
import { useApiClient } from '@/api/apiClient';

const { api } = useApiClient();
const route = useRoute();
const confirm = useConfirm();

const product = ref<ProductDto>();

const selectedImages = ref<string[]>([]);

const detailImageSizePx = 400;

const downloadUrl = computed(() => {
  let query = '';
  if (selectedImages.value.length) {
    query = '?' + selectedImages.value.map(id => `id=${id}`).join('&');
  }
  return `/api/products/${route.params.id}/download-images${query}`;
});

async function loadProduct() {
  const { data, error } = await api.productControllerGet({
    path: { id: route.params.id.toString() },
  });
  if (error) {
    throw new Error('Failed to load product');
  }
  product.value = data;
}
loadProduct();

const images = ref<ImageDto[]>([]);

async function loadImages() {
  const { data, error } = await api.productControllerGetImages({
    path: { productId: route.params.id.toString() },
  });
  if (error || !data) {
    throw new Error('Failed to load images');
  }
  images.value = data;
}

loadImages();

function confirmDeleteImage(image: ImageDto) {
  confirm.require({
    message: `Are you sure you want to delete this image?`,
    header: 'Delete Image',
    icon: 'pi pi-trash',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: () => deleteImage(image),
  });
}

async function deleteImage(image: ImageDto) {
  const { error } = await api.productControllerDeleteImage({
    path: {
      productId: route.params.id.toString(),
      imageId: image.id,
    },
  });
  if (error) {
    throw new Error('Failed to delete image');
  }
  images.value = images.value.filter(i => i.id !== image.id);
}

async function toggleImageAttention(image: ImageDto) {
  const attention = !image.attention;
  const { error } = await api.productControllerSetImageAttention({
    path: {
      productId: route.params.id.toString(),
      imageId: image.id,
    },
    body: {
      attention,
    },
  });
  if (error) {
    throw new Error('Failed to set image attention');
  }
  image.attention = attention;
}
</script>

<style scoped>
.product-detail-grid {
  --detail-image-size: 400px;
}

.product-detail-grid :deep(.product-detail-image-card) {
  width: var(--detail-image-size);
  min-width: var(--detail-image-size);
  max-width: var(--detail-image-size);
}
</style>
